import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { HOME } from "@/constants/testIds";

const YT_ID = "0qVlPcJp7c4";
const YT_SRC = `https://www.youtube.com/embed/${YT_ID}?autoplay=1&mute=1&loop=1&playlist=${YT_ID}&controls=0&modestbranding=1&showinfo=0&rel=0&iv_load_policy=3&disablekb=1&playsinline=1&fs=0&cc_load_policy=0`;

/**
 * Line-by-line masked reveal: each line sits behind its mask which lifts up.
 */
const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%", opacity: 0 }}
      animate={{ y: "0%", opacity: 1 }}
      transition={{ duration: 1.1, delay, ease: [0.19, 1, 0.22, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const [iframeReady, setIframeReady] = useState(false);
  const [time, setTime] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setIframeReady(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const opts = { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" };
      setTime(now.toLocaleTimeString("en-GB", opts) + " UTC");
    };
    update();
    const i = setInterval(update, 30000);
    return () => clearInterval(i);
  }, []);

  return (
    <section id="top" className="relative w-full h-[100svh] min-h-[720px] overflow-hidden bg-black">
      {/* YouTube background video (muted, autoplay, looped) */}
      <div
        data-testid={HOME.heroVideo}
        className="absolute inset-0 w-full h-full pointer-events-none"
      >
        {iframeReady && (
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full"
            style={{ aspectRatio: "16/9" }}
          >
            <iframe
              src={YT_SRC}
              title="Mind Body Soul — ambience"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              frameBorder="0"
              className="absolute inset-0 w-full h-full scale-[1.35]"
              tabIndex={-1}
            />
          </div>
        )}
        {/* Overlays: grain, vignette, gradient darkening */}
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.55)_100%)]" />
        <div className="absolute inset-0 opacity-[0.09] mix-blend-overlay grain-overlay" />
      </div>

      {/* HUD frame */}
      <div className="pointer-events-none absolute inset-0 z-10">
        {/* Corner crosshairs */}
        <div className="absolute top-24 left-6 md:left-10 flex flex-col gap-2">
          <span className="w-6 h-px bg-white/40" />
          <span className="h-6 w-px bg-white/40" />
        </div>
        <div className="absolute top-24 right-6 md:right-10 flex flex-col items-end gap-2">
          <span className="w-6 h-px bg-white/40" />
          <span className="h-6 w-px bg-white/40" />
        </div>
        <div className="absolute bottom-10 left-6 md:left-10 font-mono text-[10px] tracking-[0.3em] text-silver-300/80">
          N 34°02&apos; · W 118°15&apos;
          <span className="mx-3 text-white/30">/</span>
          {time}
        </div>
        <div className="absolute bottom-10 right-6 md:right-10 font-mono text-[10px] tracking-[0.3em] text-silver-300/80">
          CH.01 — PROLOGUE
        </div>
      </div>

      {/* Content */}
      <div className="relative z-20 h-full flex flex-col justify-end pb-28 md:pb-32 px-6 md:px-10">
        <div className="max-w-[1600px] mx-auto w-full">
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="flex items-center gap-3 mb-6 md:mb-8"
          >
            <span className="w-8 h-px bg-silver-300" />
            <span className="font-mono text-[10px] md:text-[11px] tracking-[0.4em] text-silver-200">
              A DISCIPLINE OF STILLNESS & STRENGTH
            </span>
          </motion.div>

          {/* Editorial headline */}
          <h1
            data-testid={HOME.heroHeadline}
            className="font-serif font-light text-white leading-[0.92] tracking-[-0.02em] text-[13vw] md:text-[9.5vw] lg:text-[8.4vw]"
            style={{ fontVariationSettings: "'SOFT' 30" }}
          >
            <RevealLine delay={0.55}>
              <span className="italic font-normal text-silver-100">Proper</span>{" "}
              preparation
            </RevealLine>
            <RevealLine delay={0.75}>prevents poor</RevealLine>
            <RevealLine delay={0.95}>
              <span className="italic font-normal">performance.</span>
            </RevealLine>
          </h1>

          {/* Sub + CTA row */}
          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.35, duration: 0.9 }}
              className="md:col-span-5 text-white/70 text-[14px] md:text-[15px] leading-[1.7] max-w-md"
            >
              Apparel, programs and rituals designed for the athlete who trains
              in silence. Built in Colorado. Tested at altitude. Sworn by an
              order of quiet devotees.
            </motion.p>

            <div className="md:col-span-4 md:col-start-9 flex flex-col sm:flex-row gap-3">
              <motion.a
                data-testid={HOME.heroCta}
                href="#programs"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.45, duration: 0.9 }}
                className="group inline-flex items-center justify-between px-6 py-4 rounded-full bg-silver-100 text-black hover:bg-white transition-all duration-500 min-w-[220px]"
              >
                <span className="text-[11px] tracking-[0.32em] uppercase font-medium">
                  Begin the Ascent
                </span>
                <span className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-500">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M1 6H11M11 6L6 1M11 6L6 11"
                      stroke="currentColor"
                      strokeWidth="1.2"
                    />
                  </svg>
                </span>
              </motion.a>

              <motion.a
                href="#creed"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.55, duration: 0.9 }}
                className="inline-flex items-center gap-3 px-4 py-4 text-white/80 hover:text-white transition-colors duration-300"
              >
                <span className="w-9 h-9 rounded-full border border-white/25 flex items-center justify-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" />
                </span>
                <span className="text-[11px] tracking-[0.3em] uppercase">
                  Read the Creed
                </span>
              </motion.a>
            </div>
          </div>

          {/* Scroll cue */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.9, duration: 1 }}
            className="mt-14 flex items-center gap-3 text-silver-300/70"
          >
            <span className="font-mono text-[10px] tracking-[0.35em]">
              SCROLL
            </span>
            <motion.span
              className="block w-16 h-px bg-silver-300/60"
              style={{ transformOrigin: "left" }}
              animate={{ scaleX: [0.5, 1, 0.5] }}
              transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
